import React, { useState, useEffect, useRef } from "react";
import {
  API_BASE_URL, STATUS_LABELS, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminReviews({ user }) {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", location: "", tag: "", rating: 5, review: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewModalText, setReviewModalText] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const searchRef = useRef(null);
  const snapshotRef = useRef(null);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = [...reviews];
    if (statusFilter) {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.review || "").toLowerCase().includes(q) ||
        (r.tag || "").toLowerCase().includes(q)
      );
    }
    if (ratingFilter !== "all") {
      filtered = filtered.filter((r) => r.rating === Number(ratingFilter));
    }
    filtered.sort((a, b) => {
      const da = a.created_at || a.date || "";
      const db = b.created_at || b.date || "";
      return sortOrder === "newest" ? db.localeCompare(da) : da.localeCompare(db);
    });
    setFilteredReviews(filtered);
  }, [reviews, statusFilter, searchQuery, ratingFilter, sortOrder]);

  const loadData = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const [reviewsRes, contentRes] = await Promise.all([
        fetch(`${API_BASE_URL}/refund/reviews`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/content/refundPolicy`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const reviewsData = await reviewsRes.json();
      if (reviewsRes.ok) {
        setReviews(reviewsData.reviews || []);
      }
      const contentData = await contentRes.json();
      if (contentData && contentData.section) {
        snapshotRef.current = JSON.stringify(contentData.section.content);
        setSectionInfo({ version: contentData.section.version, updatedAt: contentData.section.updatedAt });
      }
    } catch (err) {
      console.error("Load reviews error:", err);
      setMessage("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/refund/reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setReviews(data.reviews || []);
      }
    } catch (err) {
      console.error("Fetch reviews error:", err);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl("reviews"), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/reviews/push-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    try {
      const contentRes = await fetch(`${API_BASE_URL}/content/refundPolicy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const contentData = await contentRes.json();
      if (!contentData || !contentData.section) {
        setMessage("Failed to load content for publishing.");
        setSaving(false);
        return;
      }
      const content = contentData.section.content;
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription("Refund Policy"),
          changes: {
            type: "content_update",
            sectionKey: "refundPolicy",
            content: content,
          },
          sectionKey: "refundPolicy",
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        setShowSuccessOverlay(true);
        snapshotRef.current = JSON.stringify(content);
        setSectionInfo((prev) => ({
          ...prev,
          version: (prev?.version || 0) + 1,
          updatedAt: new Date().toISOString(),
        }));
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/refund/reviews/${id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Changes made");
        fetchReviews();
      } else {
        setMessage(data.error || "Failed to approve.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const handleReject = async (id, reason) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/refund/reviews/${id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Review rejected!");
        fetchReviews();
      } else {
        setMessage(data.error || "Failed to reject.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setEditForm({
      name: review.name || "",
      location: review.location || "",
      tag: review.tag || "Visa",
      rating: review.rating || 5,
      review: review.review || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/refund/testimonials/${editingReview.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Testimonial updated!");
        setEditingReview(null);
        fetchReviews();
      } else {
        setMessage(data.error || "Failed to update.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const handleDeleteClick = (review) => {
    setShowDeleteConfirm(review);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/refund/testimonials/${showDeleteConfirm.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Testimonial removed from content.");
        setShowDeleteConfirm(null);
        fetchReviews();
      } else {
        setMessage(data.error || "Failed to delete.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const renderStars = (count) => {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} style={{ color: i < count ? "#f59e0b" : "#d1d5db" }}>
            ★
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">Loading reviews...</div>;
  }

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3" style={{ marginRight: "auto" }}>Reviews <span className="AdminCountBadge">{filteredReviews.length}</span></h3>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            className="AdminButton AdminButtonSmall AdminSubmitBtn"
            onClick={handlePublish}
            disabled={saving}
            style={{ whiteSpace: "nowrap" }}
          >
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button
            className="AdminButton AdminButtonSmall AdminSheetBtn"
            onClick={handleOpenSheet}
          >
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") || message.includes("approved") || message.includes("rejected") || message.includes("successfully") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminFilterBar" style={{ flexWrap: "nowrap", marginBottom: "24px", justifyContent: "flex-end", alignItems: "center" }}>
        <span style={{ marginRight: "auto", fontWeight: 600, fontSize: 15, color: "#1B2A4A" }}>
          Refund Review
        </span>
        <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`} style={{ flexShrink: 0 }}>
          <span className="AdminPillSearchIcon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            ref={searchRef}
            type="text"
            className="AdminPillSearch"
            placeholder="Search name, review, tag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
             onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
          />
          {!searchOpen && (
            <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
          )}
        </div>
        <div className="AdminPillFilterItem">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="AdminPillSelect"
            style={{ width: "auto", minWidth: 0, maxWidth: "160px" }}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
        <div className="AdminPillFilterItem">
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="AdminPillSelect"
            style={{ width: "auto", minWidth: 0, maxWidth: "120px" }}
          >
            <option value="all">All</option>
            <option value="5">5</option>
            <option value="4">4</option>
            <option value="3">3</option>
            <option value="2">2</option>
            <option value="1">1</option>
          </select>
        </div>
        <button className="AdminButton AdminButtonSmall AdminTabBtn" onClick={() => setSortOrder(prev => prev === "newest" ? "oldest" : "newest")}>
          {sortOrder === "newest" ? "Newest" : "Oldest"}
        </button>
      </div>

      <div className="AdminTableScroll">
        <table className="AdminTable">
          <thead>
            <tr>
              <th className="AdminThOrder" style={{ whiteSpace: "nowrap", minWidth: 130 }}>Date</th>
              <th>Name</th>
              <th>Location</th>
              <th>Tag</th>
              <th>Rating</th>
              <th>Review</th>
              <th>Status</th>
              <th className="AdminThActions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReviews.length === 0 ? (
              <tr>
                <td colSpan="8">
                  <div className="AdminEmptyState">No reviews found</div>
                </td>
              </tr>
            ) : (
              [...filteredReviews]
                .map((review) => {
                return (
                  <tr key={review.id} onClick={() => { setSelectedReview(review); setShowDetailsModal(true); }} style={{ cursor: "pointer", transition: "filter 0.15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.filter = "brightness(0.97)"}
                    onMouseLeave={(e) => e.currentTarget.style.filter = "none"}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>{review.date}</td>
                    <td style={{ fontWeight: 500 }}>{review.name}</td>
                    <td>{review.location || "-"}</td>
                    <td>
                      <span style={{
                        padding: "2px 8px", borderRadius: "4px", fontSize: "12px",
                        backgroundColor: "#e0f2fe", color: "#0369a1"
                      }}>
                        {review.tag || "Visa"}
                      </span>
                    </td>
                    <td>{renderStars(review.rating)}</td>
                    <td style={{ maxWidth: "280px", wordBreak: "break-word" }}>
                      {review.review ? (
                        <span
                          style={{ cursor: "pointer", color: "#2563eb", textDecoration: "underline", textDecorationStyle: "dotted" }}
                          onClick={() => { setReviewModalText(review.review); setShowReviewModal(true); }}
                        >
                          {review.review.length > 80 ? review.review.substring(0, 80) + "..." : review.review}
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      <span style={{
                        padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "500",
                        backgroundColor: review.status === "approved" ? "#d1fae5" : review.status === "rejected" ? "#fee2e2" : "#fef3c7",
                        color: review.status === "approved" ? "#065f46" : review.status === "rejected" ? "#991b1b" : "#92400e"
                      }}>
                        {STATUS_LABELS[review.status] || review.status}
                      </span>
                    </td>
                    <td>
                      <div className="AdminActionBtns">
                        {(review.status === "pending" || review.status === "rejected") && (
                          <button
                            className="AdminIconBtn AdminIconBtnBlue"
                            onClick={() => handleApprove(review.id)}
                            title="Approve"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </button>
                        )}
                        {(review.status === "pending" || review.status === "approved") && (
                          <button
                            className="AdminIconBtn AdminIconBtnRed"
                            onClick={() => {
                              const reason = window.prompt("Reason for rejection (optional):");
                              if (reason !== null) {
                                handleReject(review.id, reason);
                              }
                            }}
                            title="Reject"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={e => e.stopPropagation()}>
            <div className="AdminText48">{"\u2705"}</div>
            <h3 className="AdminModalTitle AdminMb4">Changes Published</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B20">
              Your content changes have been published live.
            </p>
            <div className="AdminFlexCenter">
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={() => setShowSuccessOverlay(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {editingReview && (
        <div className="AdminModal" onClick={() => setEditingReview(null)}>
          <div className="AdminModalContent AdminModalMaxW520" onClick={e => e.stopPropagation()}>
            <h3 className="AdminModalTitle AdminMb4">Edit Testimonial</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Name</label>
                <input className="AdminInput" style={{ width: "100%" }} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Location</label>
                <input className="AdminInput" style={{ width: "100%" }} value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Tag</label>
                <input className="AdminInput" style={{ width: "100%" }} value={editForm.tag} onChange={e => setEditForm(f => ({ ...f, tag: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Rating</label>
                <select className="AdminInput" style={{ width: "100%" }} value={editForm.rating} onChange={e => setEditForm(f => ({ ...f, rating: Number(e.target.value) }))}>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} Star{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Review</label>
                <textarea className="AdminInput" style={{ width: "100%", minHeight: 80, resize: "vertical" }} value={editForm.review} onChange={e => setEditForm(f => ({ ...f, review: e.target.value }))} />
              </div>
            </div>
            <div className="AdminFlexCenter" style={{ marginTop: 20, gap: 8 }}>
              <button className="AdminButton AdminButtonSecondary" onClick={() => setEditingReview(null)}>Cancel</button>
              <button className="AdminButton AdminSubmitBtn" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="AdminModal" onClick={() => setShowDeleteConfirm(null)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={e => e.stopPropagation()}>
            <div className="AdminText48" style={{ fontSize: 40 }}>{"\u26A0\uFE0F"}</div>
            <h3 className="AdminModalTitle AdminMb4">Delete Testimonial</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B20">
              Remove "{showDeleteConfirm.name}"&apos;s testimonial from the refund policy content? This cannot be undone.
            </p>
            <div className="AdminFlexCenter" style={{ gap: 8 }}>
              <button className="AdminButton AdminButtonSecondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button className="AdminButton" style={{ backgroundColor: "#dc2626", color: "#fff" }} onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review Details Modal ── */}
      {showDetailsModal && selectedReview && (
        <div className="AdminModal" onClick={() => setShowDetailsModal(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 600, padding: "24px 28px", maxHeight: "85vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="AdminModalTitle" style={{ marginBottom: 18 }}>Review Details</h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Date</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedReview.date || "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Name</label>
                <div style={{ fontSize: 14, color: "#1e293b", fontWeight: 500 }}>{selectedReview.name || "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Location</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>{selectedReview.location || "—"}</div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Tag</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: "4px", fontSize: "12px",
                    backgroundColor: "#e0f2fe", color: "#0369a1"
                  }}>
                    {selectedReview.tag || "Visa"}
                  </span>
                </div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Rating</label>
                <div style={{ fontSize: 14, color: "#1e293b" }}>
                  <div style={{ display: "flex", gap: "2px" }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} style={{ color: i < selectedReview.rating ? "#f59e0b" : "#d1d5db" }}>★</span>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Status</label>
                <div>
                  <span style={{
                    padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "500",
                    backgroundColor: selectedReview.status === "approved" ? "#d1fae5" : selectedReview.status === "rejected" ? "#fee2e2" : "#fef3c7",
                    color: selectedReview.status === "approved" ? "#065f46" : selectedReview.status === "rejected" ? "#991b1b" : "#92400e"
                  }}>
                    {STATUS_LABELS[selectedReview.status] || selectedReview.status}
                  </span>
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1", borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 2 }}>Review</label>
                <div style={{ fontSize: 14, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{selectedReview.review || "—"}</div>
              </div>
            </div>

            <div className="AdminFlexCenter" style={{ marginTop: 20 }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowDetailsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Full Review Modal ── */}
      {showReviewModal && (
        <div className="AdminModal" onClick={() => setShowReviewModal(false)}>
          <div className="AdminModalContent AdminModalMaxW520" onClick={(e) => e.stopPropagation()}>
            <h3 className="AdminModalTitle">Review</h3>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 400, overflowY: "auto" }}>
              {reviewModalText}
            </div>
            <div className="AdminFlexCenter" style={{ marginTop: 20 }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowReviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReviews;
